import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { CITIES, cityToSlug } from './config/cities';
import { trackPageView } from './analytics';

// Lazy-load page components for code splitting
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = React.lazy(() => import('./pages/SignupPage').then(m => ({ default: m.SignupPage })));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const MyToursPage = React.lazy(() => import('./pages/MyToursPage').then(m => ({ default: m.MyToursPage })));
const TourCreatePage = React.lazy(() => import('./pages/TourCreatePage').then(m => ({ default: m.TourCreatePage })));
const TourEditPage = React.lazy(() => import('./pages/TourEditPage').then(m => ({ default: m.TourEditPage })));
const TourBrowsePage = React.lazy(() => import('./pages/TourBrowsePage').then(m => ({ default: m.TourBrowsePage })));
const TourDetailPage = React.lazy(() => import('./pages/TourDetailPage').then(m => ({ default: m.TourDetailPage })));
const TakeTourNavigatePage = React.lazy(() => import('./pages/TakeTourNavigatePage').then(m => ({ default: m.TakeTourNavigatePage })));
const TakeTourWaypointPage = React.lazy(() => import('./pages/TakeTourWaypointPage').then(m => ({ default: m.TakeTourWaypointPage })));
const TourCompletePage = React.lazy(() => import('./pages/TourCompletePage').then(m => ({ default: m.TourCompletePage })));
const TourReviewPage = React.lazy(() => import('./pages/TourReviewPage').then(m => ({ default: m.TourReviewPage })));
const CityPage = React.lazy(() => import('./pages/CityPage').then(m => ({ default: m.CityPage })));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const AboutPage = React.lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const TermsPage = React.lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const GuidelinesPage = React.lazy(() => import('./pages/GuidelinesPage').then(m => ({ default: m.GuidelinesPage })));
const SafetyPage = React.lazy(() => import('./pages/SafetyPage').then(m => ({ default: m.SafetyPage })));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const AuthCallbackPage = React.lazy(() => import('./pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));

// Layout wrapper component — also tracks page views on every route change
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
    trackPageView(location.pathname)
  }, [location.pathname])

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main id="main-content" className="flex-1">
        <Suspense>{children}</Suspense>
      </main>
      <Footer />
    </div>
  )
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Layout>
        <LandingPage />
      </Layout>
    ),
  },
  {
    path: '/login',
    element: (
      <Layout>
        <LoginPage />
      </Layout>
    ),
  },
  {
    path: '/signup',
    element: (
      <Layout>
        <SignupPage />
      </Layout>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Layout>
        <ForgotPasswordPage />
      </Layout>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Layout>
        <ResetPasswordPage />
      </Layout>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <Layout>
        <VerifyEmailPage />
      </Layout>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <Layout>
        <AuthCallbackPage />
      </Layout>
    ),
  },
  {
    path: '/profile',
    element: (
      <Layout>
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      </Layout>
    ),
  },
  {
    path: '/tours',
    element: (
      <Layout>
        <TourBrowsePage />
      </Layout>
    ),
  },
  {
    path: '/tours/:tourId',
    element: (
      <Layout>
        <TourDetailPage />
      </Layout>
    ),
  },
  {
    path: '/tours/:tourId/take/navigate/:waypointId',
    element: (
      <Layout>
        <TakeTourNavigatePage />
      </Layout>
    ),
  },
  {
    path: '/tours/:tourId/take/waypoint/:waypointId',
    element: (
      <Layout>
        <TakeTourWaypointPage />
      </Layout>
    ),
  },
  {
    path: '/tours/:tourId/take/review',
    element: (
      <Layout>
        <TourReviewPage />
      </Layout>
    ),
  },
  {
    path: '/tours/:tourId/take/complete',
    element: (
      <Layout>
        <TourCompletePage />
      </Layout>
    ),
  },
  {
    path: '/my-tours',
    element: (
      <Layout>
        <ProtectedRoute requireRole="local_expert">
          <MyToursPage />
        </ProtectedRoute>
      </Layout>
    ),
  },
  {
    path: '/my-tours/new',
    element: (
      <Layout>
        <ProtectedRoute requireRole="local_expert">
          <TourCreatePage />
        </ProtectedRoute>
      </Layout>
    ),
  },
  {
    path: '/my-tours/:tourId/edit',
    element: (
      <Layout>
        <ProtectedRoute requireRole="local_expert">
          <TourEditPage />
        </ProtectedRoute>
      </Layout>
    ),
  },
  {
    path: '/about',
    element: (
      <Layout>
        <AboutPage />
      </Layout>
    ),
  },
  {
    path: '/terms',
    element: (
      <Layout>
        <TermsPage />
      </Layout>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Layout>
        <PrivacyPage />
      </Layout>
    ),
  },
  {
    path: '/guidelines',
    element: (
      <Layout>
        <GuidelinesPage />
      </Layout>
    ),
  },
  {
    path: '/safety',
    element: (
      <Layout>
        <SafetyPage />
      </Layout>
    ),
  },
  ...CITIES.map((city) => ({
    path: `/${cityToSlug(city.name)}`,
    element: (
      <Layout>
        <CityPage />
      </Layout>
    ),
  })),
  {
    path: '*',
    element: (
      <Layout>
        <NotFoundPage />
      </Layout>
    ),
  },
]);

export const Router: React.FC = () => {
  return <RouterProvider router={router} />;
};
